/**
 * PocketFiler - Referral Program Tab
 * Shows user's referral code + stats, easy share.
 * Add to Settings page as "Referrals" tab.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { toast } from 'sonner';
import { Copy, Gift, Users, Check, Share2, Trophy, Link2 } from 'lucide-react';


export default function ReferralTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const d = await api.get('/referrals/me');
      setData(d);
    } catch (e) {
      toast.error('Failed to load referrals');
    }
    setLoading(false);
  };

  const handleCopy = () => {
    if (!data?.share_url) return;
    navigator.clipboard.writeText(data.share_url);
    setCopied(true);
    toast.success(t('referrals.linkCopied') || 'Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!data?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'PocketFiler',
          text: 'Try PocketFiler — AI contracts, document management, and more. Use my link for free access:',
          url: data.share_url,
        });
      } catch (e) {}
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  if (!data) {
    return <div className="p-8 text-center text-muted-foreground">Unable to load referrals</div>;
  }

  const { code, share_url, stats, recent_referrals, reward_description } = data;

  return (
    <div className="space-y-6">
      {/* Hero card */}
      <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="h-12 w-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center shrink-0">
            <Gift className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-1">{t('referrals.title') || 'Invite friends, earn free months'}</h2>
            <p className="text-sm text-muted-foreground">{reward_description}</p>
          </div>
        </div>

        {/* Share URL */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">{t('referrals.yourLink') || 'Your referral link'}</label>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-lg border bg-background px-3 py-2.5 text-sm font-mono text-muted-foreground">
              <Link2 className="h-4 w-4 shrink-0" />
              <span className="truncate">{share_url}</span>
            </div>
            <button
              onClick={handleCopy}
              className="px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1.5"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? (t('referrals.copied') || 'Copied!') : (t('referrals.copy') || 'Copy')}
            </button>
            <button
              onClick={handleShare}
              className="px-4 py-2.5 rounded-lg border font-semibold text-sm hover:bg-muted transition-colors flex items-center gap-1.5"
            >
              <Share2 className="h-4 w-4" />
              {t('referrals.share') || 'Share'}
            </button>
          </div>
        </div>

        {/* Code only */}
        <p className="text-xs text-muted-foreground mt-3">
          {t('referrals.orCode') || 'Or share your code:'} <code className="px-1.5 py-0.5 bg-muted rounded font-mono font-semibold">{code}</code>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-background p-4 text-center">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2">
            <Users className="h-5 w-5" />
          </div>
          <p className="text-2xl font-bold">{stats.total_invites}</p>
          <p className="text-xs text-muted-foreground">{t('referrals.totalInvites') || 'Total invites'}</p>
        </div>
        <div className="rounded-xl border bg-background p-4 text-center">
          <div className="h-10 w-10 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center mx-auto mb-2">
            <Check className="h-5 w-5" />
          </div>
          <p className="text-2xl font-bold">{stats.verified_signups}</p>
          <p className="text-xs text-muted-foreground">{t('referrals.verified') || 'Verified'}</p>
        </div>
        <div className="rounded-xl border bg-background p-4 text-center">
          <div className="h-10 w-10 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto mb-2">
            <Trophy className="h-5 w-5" />
          </div>
          <p className="text-2xl font-bold">{stats.months_earned}</p>
          <p className="text-xs text-muted-foreground">{t('referrals.monthsEarned') || 'Months earned'}</p>
        </div>
        <div className="rounded-xl border bg-background p-4 text-center">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2">
            <Gift className="h-5 w-5" />
          </div>
          <p className="text-2xl font-bold">{stats.months_available}</p>
          <p className="text-xs text-muted-foreground">{t('referrals.available') || 'Available'}</p>
        </div>
      </div>

      {/* Recent referrals */}
      {recent_referrals?.length > 0 && (
        <div className="rounded-xl border bg-background p-4">
          <h3 className="text-sm font-semibold mb-3">{t('referrals.recentSignups') || 'Recent signups'}</h3>
          <div className="space-y-2">
            {recent_referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                    {r.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <span>{r.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {r.verified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <Check className="h-3 w-3" /> Verified
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Pending verification</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {r.joined ? new Date(r.joined).toLocaleDateString() : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="rounded-xl bg-muted/30 border p-4">
        <h3 className="text-sm font-semibold mb-3">{t('referrals.howItWorks') || 'How it works'}</h3>
        <ol className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-xs font-semibold flex items-center justify-center shrink-0">1</span>
            Share your link with a friend, client, or colleague
          </li>
          <li className="flex items-start gap-2">
            <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-xs font-semibold flex items-center justify-center shrink-0">2</span>
            They sign up and verify their email
          </li>
          <li className="flex items-start gap-2">
            <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-xs font-semibold flex items-center justify-center shrink-0">3</span>
            You get 1 free month credited to your account (up to 12 months)
          </li>
        </ol>
      </div>
    </div>
  );
}
